import React from 'react';
import { useSvgPreload } from '../../../hooks/useSvgPreload';
import type { OptimizedSvgProps } from './types/types';


export const OptimizedSvg: React.FC<OptimizedSvgProps> = ({
  src,
  alt,
  className = '',
  style = {},
  width,
  height,
  onLoad,
  onError,
  cacheKey,
  // fallbackSrc,
  // preserveAspectRatio = 'xMidYMid meet'
}) => {
  const { svgContent, isLoaded, hasError } = useSvgPreload(src, {
    onLoad,
    onError,
    cacheKey: cacheKey || `svg-${src}`
  });

  if (!isLoaded && !hasError) {
    return (
      <div 
        className={`animate-pulse bg-gray-200 ${className}`}
        style={{ width, height, ...style }}
        aria-label={`Loading ${alt}`}
      />
    );
  }

  if (hasError && !svgContent) {
    return (
      <div 
        className={`bg-gray-100 flex items-center justify-center ${className}`}
        style={{ width, height, ...style }}
        aria-label={`Failed to load ${alt}`}
      >
        <span className="text-gray-500 text-sm">Failed to load image</span>
      </div>
    );
  }

  return (
    <div
      className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      style={{
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...style
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
      role="img"
      aria-label={alt}
      ref={(el) => {
        if (el) {
          const svg = el.querySelector('svg');
          if (svg) {
            // Remove width/height attributes that override styles
            svg.removeAttribute('width');
            svg.removeAttribute('height');
            // Set styles to fill container
            svg.style.width = '100%';
            svg.style.height = '100%';
            svg.style.maxWidth = '100%';
            svg.style.maxHeight = '100%';
            svg.style.display = 'block';
          }
        }
      }}
    />
  );
}; 